import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Search, Ticket, Calendar, Users, CheckCircle, Clock, 
  FileText, Download, Send, Printer, RefreshCw, MoreVertical,
  Eye, Play, Square, Trash2, Crown, UserCheck, AlertCircle, Share2,
  CreditCard, DollarSign, ChevronLeft
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';

const TOKEN_TYPES = [
  { value: 'wajebaat', label: 'Wajebaat Takhmeen' },
  { value: 'fmb', label: 'FMB Takhmeen' },
  { value: 'payment', label: 'Payment Token' }
];

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'vip', label: 'VIP (1-10)' }
];

export default function TokenEventsPage() {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showQuickGenerate, setShowQuickGenerate] = useState(false);
  const [quickGenerateEvent, setQuickGenerateEvent] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/token-events?status=${activeTab}`);
      setEvents(response.data.events || []);
    } catch (error) {
      toast.error('Failed to load token events');
    } finally {
      setLoading(false);
    }
  }, [api, activeTab]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleViewEvent = (event) => {
    navigate(`/token-events/${event.id}`);
  };

  const handleQuickGenerate = (event) => {
    if (!event.is_active) {
      toast.error('Cannot generate tokens for inactive event');
      return;
    }
    if (event.is_completed) {
      toast.error('Cannot generate tokens for completed event');
      return;
    }
    setQuickGenerateEvent(event);
    setShowQuickGenerate(true);
  };

  const handleCompleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to mark this event as completed? No new tokens can be generated after this.')) {
      return;
    }
    try {
      await api.put(`/token-events/${eventId}/complete`);
      toast.success('Event marked as completed');
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete event');
    }
  };

  const handleToggleActive = async (eventId, currentActive) => {
    try {
      await api.put(`/token-events/${eventId}`, { is_active: !currentActive });
      toast.success(`Event ${currentActive ? 'deactivated' : 'activated'}`);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update event status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-navy flex items-center gap-2">
            <Ticket className="w-6 h-6 sm:w-7 sm:h-7 text-gold" />
            Token Events
          </h1>
          <p className="text-slate-600 text-xs sm:text-sm mt-1 hidden sm:block">Create and manage token events</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-navy hover:bg-navy-light w-full sm:w-auto" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto flex overflow-x-auto">
          <TabsTrigger value="active" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none">
            <Play className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden xs:inline">Active</span>
            <span className="xs:hidden">Active</span>
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none">
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>Done</span>
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none">
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>All</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-navy" />
            </div>
          ) : events.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Ticket className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No {activeTab} events found</p>
                {isAdmin && activeTab === 'active' && (
                  <Button onClick={() => setShowCreateDialog(true)} className="mt-4 bg-navy hover:bg-navy-light">
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Event
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {events.map((event) => (
                <Card key={event.id} className="border-0 shadow-card hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-2 p-3 sm:p-6">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg font-semibold text-navy">{event.name}</CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewEvent(event)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View & Manage
                          </DropdownMenuItem>
                          {isAdmin && !event.is_completed && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleToggleActive(event.id, event.is_active)}>
                                {event.is_active ? (
                                  <><Square className="w-4 h-4 mr-2" />Deactivate</>
                                ) : (
                                  <><Play className="w-4 h-4 mr-2" />Activate</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleCompleteEvent(event.id)} className="text-amber-600">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Mark Complete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="flex gap-2 mt-1">
                      {event.is_completed ? (
                        <Badge className="bg-slate-500">Completed</Badge>
                      ) : event.is_active ? (
                        <Badge className="bg-emerald-500">Active</Badge>
                      ) : (
                        <Badge className="bg-amber-500">Inactive</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Duration:</span>
                        <span className="font-medium">{event.start_date} → {event.expected_end_date}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-navy">{event.token_count || 0}</p>
                          <p className="text-xs text-slate-500">Total</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-amber-500">{event.pending_count || 0}</p>
                          <p className="text-xs text-slate-500">Pending</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-500">{event.completed_count || 0}</p>
                          <p className="text-xs text-slate-500">Done</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button 
                          onClick={() => handleQuickGenerate(event)} 
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`quick-generate-${event.id}`}
                        >
                          <Ticket className="w-4 h-4 mr-2" />
                          Generate Token
                        </Button>
                        <Button 
                          onClick={() => handleViewEvent(event)} 
                          variant="outline"
                          className="flex-1"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Open Event
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Quick Generate Dialog */}
      {showQuickGenerate && quickGenerateEvent && (
        <Dialog open={showQuickGenerate} onOpenChange={() => setShowQuickGenerate(false)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Ticket className="w-5 h-5 text-emerald-600" />
                <span className="break-words">Quick Generate - {quickGenerateEvent.name}</span>
              </DialogTitle>
            </DialogHeader>
            <TokenGenerationForm 
              eventId={quickGenerateEvent.id} 
              api={api} 
              onSuccess={() => { fetchEvents(); }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Create Event Dialog */}
      {showCreateDialog && (
        <CreateEventDialog
          api={api}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => { fetchEvents(); setShowCreateDialog(false); }}
        />
      )}
    </div>
  );
}

// Create Event Dialog
function CreateEventDialog({ api, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    start_date: new Date().toISOString().split('T')[0],
    expected_end_date: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name || !formData.start_date || !formData.expected_end_date) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/token-events', formData);
      toast.success('Token event created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5 text-gold" />
            Create Token Event
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label className="form-label">Event Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Wajebaat Bethak 1447H"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Start Date *</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
              />
            </div>
            <div>
              <Label className="form-label">Expected End Date *</Label>
              <Input
                type="date"
                value={formData.expected_end_date}
                onChange={(e) => setFormData({...formData, expected_end_date: e.target.value})}
              />
            </div>
          </div>
          
          <div>
            <Label className="form-label">Description (Optional)</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Event description..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit} disabled={loading} className="bg-navy hover:bg-navy-light">
            {loading ? 'Creating...' : 'Create Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Event Management Dialog (Full Screen)
function EventManagementDialog({ event, api, user, isAdmin, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState('generate');
  const [dashboard, setDashboard] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filters, setFilters] = useState({
    status: '',
    token_type: '',
    category: '',
    mohalla: '',
    search: ''
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTokens, setSelectedTokens] = useState([]);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  
  // Auto-refresh state - DISABLED by default to prevent scroll reset
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Bluetooth Printer State for bulk print
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [bluetoothConnecting, setBluetoothConnecting] = useState(false);
  const [bluetoothPrinting, setBluetoothPrinting] = useState(false);
  const [printerCharacteristic, setPrinterCharacteristic] = useState(null);

  // Check if Web Bluetooth is available
  const isBluetoothAvailable = typeof navigator !== 'undefined' && navigator.bluetooth;

  // Connect to Bluetooth Printer
  const connectBluetoothPrinter = async () => {
    if (!isBluetoothAvailable) {
      toast.error('Bluetooth not available. Use Chrome on Android.');
      return false;
    }

    try {
      setBluetoothConnecting(true);
      
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          '000018f0-0000-1000-8000-00805f9b34fb',
          '49535343-fe7d-4ae5-8fa9-9fafd205e455',
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
          '0000ff00-0000-1000-8000-00805f9b34fb',
          '0000ffe0-0000-1000-8000-00805f9b34fb',
          '0000fee7-0000-1000-8000-00805f9b34fb',
          '00001101-0000-1000-8000-00805f9b34fb',
          '49535343-8841-43f4-a8d4-ecbe34729bb3',
          '49535343-1e4d-4bd9-ba61-23c647249616',
          '0000fff0-0000-1000-8000-00805f9b34fb',
        ]
      });

      setBluetoothDevice(device);
      toast.info(`Connecting to ${device.name || 'Printer'}...`);
      
      let server;
      try {
        server = await device.gatt.connect();
      } catch (gattError) {
        console.error('GATT connect error:', gattError);
        toast.error('Cannot connect. Your printer may use Bluetooth Classic (not BLE). Try browser print instead.');
        setBluetoothConnecting(false);
        return false;
      }
      
      const characteristic = await findPrinterCharacteristic(server);
      
      if (characteristic) {
        setPrinterCharacteristic(characteristic);
        toast.success('Printer connected!');
        
        device.addEventListener('gattserverdisconnected', () => {
          setBluetoothDevice(null);
          setPrinterCharacteristic(null);
          toast.info('Printer disconnected');
        });
        
        return true;
      } else {
        toast.error('Printer found but not BLE compatible. Use browser print instead.');
        return false;
      }
    } catch (error) {
      console.error('Bluetooth error:', error);
      if (error.name === 'NotFoundError') {
        toast.info('No printer selected');
      } else {
        toast.error('Connection failed. Your printer may not support BLE.');
      }
      return false;
    } finally {
      setBluetoothConnecting(false);
    }
  };

  // Find writable characteristic for printing
  const findPrinterCharacteristic = async (server) => {
    const serviceUUIDs = [
      '000018f0-0000-1000-8000-00805f9b34fb',
      '49535343-fe7d-4ae5-8fa9-9fafd205e455',
      'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
      '0000ff00-0000-1000-8000-00805f9b34fb',
      '0000ffe0-0000-1000-8000-00805f9b34fb',
      '0000fff0-0000-1000-8000-00805f9b34fb',
      '0000fee7-0000-1000-8000-00805f9b34fb',
    ];
    
    for (const serviceUUID of serviceUUIDs) {
      try {
        const service = await server.getPrimaryService(serviceUUID);
        const characteristics = await service.getCharacteristics();
        
        for (const char of characteristics) {
          const props = char.properties;
          if (props.write || props.writeWithoutResponse) {
            return char;
          }
        }
      } catch (e) {
        continue;
      }
    }
    
    try {
      const services = await server.getPrimaryServices();
      for (const service of services) {
        try {
          const characteristics = await service.getCharacteristics();
          for (const char of characteristics) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              return char;
            }
          }
        } catch (e) {
          continue;
        }
      }
    } catch (e) {
      console.log('Service enumeration failed');
    }
    
    return null;
  };

  // Generate ESC/POS commands for bulk token list
  const generateBulkTokenESCPOS = (tokensList) => {
    const encoder = new TextEncoder();
    const commands = [];
    
    const ESC = 0x1B;
    const GS = 0x1D;
    const LF = 0x0A;
    
    // Initialize printer
    commands.push(ESC, 0x40);
    
    // Header - Event Name
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(ESC, 0x45, 0x01); // Bold
    commands.push(GS, 0x21, 0x01); // Double height
    commands.push(...encoder.encode(event.name.substring(0, 24)));
    commands.push(LF);
    
    // Date
    commands.push(GS, 0x21, 0x00);
    commands.push(ESC, 0x45, 0x00);
    const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    commands.push(...encoder.encode(today));
    commands.push(LF);
    
    // Divider
    commands.push(...encoder.encode('================================'));
    commands.push(LF);
    
    // Total count
    commands.push(...encoder.encode(`Total Tokens: ${tokensList.length}`));
    commands.push(LF);
    commands.push(...encoder.encode('T=Takhmeen | P=Payment'));
    commands.push(LF);
    commands.push(...encoder.encode('--------------------------------'));
    commands.push(LF);
    
    const typeMap = {"wajebaat": "W", "fmb": "F", "payment": "P"};
    
    // Each token
    tokensList.forEach((token, idx) => {
      commands.push(ESC, 0x61, 0x00); // Left align
      
      // Token # and Type
      commands.push(ESC, 0x45, 0x01); // Bold
      commands.push(...encoder.encode(`#${token.token_number} [${typeMap[token.token_type] || 'T'}]`));
      commands.push(ESC, 0x45, 0x00);
      commands.push(LF);
      
      // Name (truncate if needed)
      const name = (token.full_name || '-').substring(0, 28);
      commands.push(...encoder.encode(name));
      commands.push(LF);
      
      // ITS and Mohalla
      commands.push(...encoder.encode(`${token.its_id} | ${token.mohalla?.substring(0, 10) || ''}`));
      commands.push(LF);
      
      // Clear Takhmeen and Payment status
      const takhmeenStatus = token.takhmeen_done ? 'Y' : 'N';
      const paymentStatus = token.payment_done ? 'Y' : 'N';
      commands.push(...encoder.encode(`T:${takhmeenStatus} | P:${paymentStatus}`));
      commands.push(LF);
      
      // Separator between tokens
      if (idx < tokensList.length - 1) {
        commands.push(...encoder.encode('- - - - - - - - - - - - - - - -'));
        commands.push(LF);
      }
    });
    
    // Footer
    commands.push(...encoder.encode('================================'));
    commands.push(LF);
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(...encoder.encode('Printed: ' + new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })));
    commands.push(LF, LF, LF, LF); // Feed paper
    
    return new Uint8Array(commands);
  };

  // Print all tokens via Bluetooth
  const handleBluetoothPrintAll = async () => {
    if (tokens.length === 0) {
      toast.error('No tokens to print');
      return;
    }

    try {
      setBluetoothPrinting(true);
      
      let characteristic = printerCharacteristic;
      
      if (!characteristic || !bluetoothDevice?.gatt?.connected) {
        toast.info('Connecting to printer...');
        const connected = await connectBluetoothPrinter();
        if (!connected) {
          setBluetoothPrinting(false);
          return;
        }
        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 500));
        characteristic = printerCharacteristic;
      }
      
      if (!characteristic) {
        toast.error('Printer not ready. Please try again.');
        setBluetoothPrinting(false);
        return;
      }
      
      toast.info(`Printing ${tokens.length} tokens...`);
      const printData = generateBulkTokenESCPOS(tokens);
      
      // Send in chunks (BLE has MTU limits)
      const chunkSize = 100;
      for (let i = 0; i < printData.length; i += chunkSize) {
        const chunk = printData.slice(i, i + chunkSize);
        try {
          if (characteristic.properties.writeWithoutResponse) {
            await characteristic.writeValueWithoutResponse(chunk);
          } else {
            await characteristic.writeValue(chunk);
          }
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (writeError) {
          console.error('Write error:', writeError);
          throw writeError;
        }
      }
      
      toast.success(`${tokens.length} tokens sent to printer!`);
    } catch (error) {
      console.error('Bluetooth print error:', error);
      toast.error('Print failed. Try browser print instead.');
    } finally {
      setBluetoothPrinting(false);
    }
  };

  // Debounce search filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search || '');
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Print Token List - Receipt style for mobile printers
  const handlePrintTokenList = () => {
    if (tokens.length === 0) {
      toast.error('No tokens to print');
      return;
    }

    const tokenTypeMap = { "wajebaat": "Waj", "fmb": "FMB", "payment": "Pay" };
    const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    
    // Generate receipt-style HTML for thermal/mobile printer (58mm width)
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Token List - ${event.name}</title>
        <style>
          @page {
            size: 58mm auto;
            margin: 2mm;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', monospace;
            font-size: 10px;
            line-height: 1.3;
            width: 54mm;
            max-width: 54mm;
            padding: 2mm;
          }
          .header {
            text-align: center;
            font-weight: bold;
            font-size: 12px;
            border-bottom: 1px dashed #000;
            padding-bottom: 3px;
            margin-bottom: 5px;
          }
          .event-name {
            font-size: 11px;
            word-wrap: break-word;
          }
          .date-info {
            font-size: 9px;
            color: #333;
            margin-top: 2px;
          }
          .summary {
            text-align: center;
            font-size: 10px;
            margin: 4px 0;
            padding: 2px;
            background: #f0f0f0;
          }
          .divider {
            border-top: 1px dashed #000;
            margin: 4px 0;
          }
          .token-item {
            border-bottom: 1px dotted #ccc;
            padding: 3px 0;
            page-break-inside: avoid;
          }
          .token-row {
            display: flex;
            justify-content: space-between;
            font-size: 9px;
          }
          .token-num {
            font-weight: bold;
            font-size: 11px;
          }
          .token-type {
            font-size: 8px;
            padding: 1px 3px;
            background: #e0e0e0;
            border-radius: 2px;
          }
          .name {
            font-weight: bold;
            font-size: 10px;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          .its {
            font-family: monospace;
            font-size: 9px;
          }
          .info {
            font-size: 8px;
            color: #555;
          }
          .status-badge {
            font-size: 8px;
            padding: 1px 3px;
            border-radius: 2px;
            margin-left: 2px;
          }
          .done { background: #d4edda; color: #155724; }
          .pending { background: #fff3cd; color: #856404; }
          .paid { background: #cce5ff; color: #004085; }
          .status-row {
            display: flex;
            gap: 4px;
            font-size: 8px;
            margin-top: 2px;
          }
          .footer {
            text-align: center;
            font-size: 8px;
            margin-top: 5px;
            border-top: 1px dashed #000;
            padding-top: 3px;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="event-name">${event.name}</div>
          <div class="date-info">Generated: ${today}</div>
        </div>
        
        <div class="summary">
          Total: ${tokens.length} tokens
        </div>
        
        <div class="divider"></div>
        
        ${tokens.map((token, idx) => `
          <div class="token-item">
            <div class="token-row">
              <span class="token-num">#${token.token_number}</span>
              <span class="token-type">${tokenTypeMap[token.token_type] || token.token_type}</span>
            </div>
            <div class="name">${token.full_name || '-'}</div>
            <div class="token-row">
              <span class="its">ITS: ${token.its_id}</span>
            </div>
            <div class="info">${token.mohalla || ''} ${token.sector ? '| S' + token.sector : ''}</div>
            <div class="status-row">
              <span class="status-badge ${token.takhmeen_done ? 'done' : 'pending'}">T: ${token.takhmeen_done ? '✓' : '✗'}</span>
              <span class="status-badge ${token.payment_done ? 'paid' : 'pending'}">P: ${token.payment_done ? '✓' : '✗'}</span>
            </div>
          </div>
        `).join('')}
        
        <div class="footer">
          T=Takhmeen | P=Payment<br/>
          Printed: ${new Date().toLocaleString('en-IN')}
        </div>
      </body>
      </html>
    `;

    // Open print dialog
    const printWindow = window.open('', '_blank', 'width=300,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    } else {
      toast.error('Please allow popups to print');
    }
  };

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get(`/token-events/${event.id}/dashboard?date=${selectedDate}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard');
    }
  }, [api, event.id, selectedDate]);

  // Fetch tokens - filtered by current/selected date
  const fetchTokens = async (statusFilter = '') => {
    try {
      setTokensLoading(true);
      const queryFilters = {
        ...filters,
        search: debouncedSearch,
        // Only filter by date if NOT searching - search should look across all dates
        ...(debouncedSearch ? {} : { date: selectedDate })
      };
      if (statusFilter) {
        queryFilters.status = statusFilter;
      }
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '100',  // Show more tokens per page
        ...Object.fromEntries(Object.entries(queryFilters).filter(([_, v]) => v))
      });
      const response = await api.get(`/token-events/${event.id}/tokens?${params}`);
      setTokens(response.data.tokens || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      toast.error('Failed to fetch tokens');
    } finally {
      setTokensLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'tokens') {
      fetchTokens('');
    } else if (activeTab === 'pending') {
      fetchTokens('pending');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, event.id, filters.token_type, filters.status, debouncedSearch, selectedDate]);

  // Auto-refresh every 10 seconds when enabled - SILENT refresh (no loading states, no scroll reset)
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(async () => {
      try {
        // Silently refresh dashboard data (no loading states)
        const dashResponse = await api.get(`/token-events/${event.id}/dashboard?date=${selectedDate}`);
        if (dashResponse.data) {
          setDashboard(dashResponse.data);
        }
        
        // Silently refresh tokens if on tokens tab (no loading states)
        if (activeTab === 'tokens' || activeTab === 'pending') {
          const queryFilters = {
            ...filters,
            search: debouncedSearch,
            date: selectedDate
          };
          if (activeTab === 'pending') {
            queryFilters.status = 'pending';
          }
          const params = new URLSearchParams({
            page: page.toString(),
            limit: '100',
            ...Object.fromEntries(Object.entries(queryFilters).filter(([_, v]) => v))
          });
          const tokenResponse = await api.get(`/token-events/${event.id}/tokens?${params}`);
          if (tokenResponse.data) {
            setTokens(tokenResponse.data.tokens || []);
            setTotalPages(tokenResponse.data.pages || 1);
          }
        }
        
        setLastRefresh(new Date());
      } catch (error) {
        console.error('Auto-refresh failed:', error);
      }
    }, 10000); // 10 seconds
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, activeTab, event?.id, selectedDate, filters, debouncedSearch, page, api]);

  // Update token status
  const handleUpdateStatus = async (tokenId, status) => {
    try {
      await api.put(`/tokens/${tokenId}/status`, { status });
      toast.success(`Token marked as ${status}`);
      if (activeTab === 'pending') {
        fetchTokens('pending');
      } else {
        fetchTokens('');
      }
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update token status');
    }
  };

  // Bulk update status
  const handleBulkStatus = async (status) => {
    if (selectedTokens.length === 0) {
      toast.error('Please select tokens first');
      return;
    }
    try {
      await api.put('/tokens/bulk-status', { token_ids: selectedTokens, status });
      toast.success(`${selectedTokens.length} tokens marked as ${status}`);
      setSelectedTokens([]);
      fetchTokens();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update tokens');
    }
  };

  // Export report
  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams({
        format,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      
      const response = await api.get(`/token-events/${event.id}/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tokens_${event.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  // Print report
  const handlePrintReport = async () => {
    try {
      const params = new URLSearchParams({
        format: 'pdf',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      
      const response = await api.get(`/token-events/${event.id}/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      toast.error('Failed to generate print preview');
    }
  };

  // Delete Event Handler
  const handleDeleteEvent = async () => {
    if (!window.confirm(`Are you sure you want to delete this event? This action cannot be undone.\n\nThis will permanently delete:\n• All related tokens\n• Dashboard data\n• Pending lists`)) {
      return;
    }
    
    try {
      await api.delete(`/token-events/${event.id}`);
      toast.success('Event deleted successfully');
      onClose();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  // State for mohalla drilldown
  const [drilldownData, setDrilldownData] = useState(null);
  const [showDrilldown, setShowDrilldown] = useState(false);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  // Fetch mohalla drilldown
  const fetchMohallaDrilldown = async (mohalla, filterType) => {
    setDrilldownLoading(true);
    try {
      const response = await api.get(`/token-events/${event.id}/mohalla-drilldown?mohalla=${encodeURIComponent(mohalla)}&filter_type=${filterType}`);
      setDrilldownData(response.data);
      setShowDrilldown(true);
    } catch (error) {
      toast.error('Failed to load details');
    } finally {
      setDrilldownLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-3 sm:p-6">
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-xl flex-wrap">
              <Ticket className="w-5 h-5 sm:w-6 sm:h-6 text-gold" />
              <span className="break-words">{event.name}</span>
              {event.is_completed ? (
                <Badge className="bg-slate-500 text-xs">Completed</Badge>
              ) : event.is_active ? (
                <Badge className="bg-emerald-500 text-xs">Active</Badge>
              ) : (
                <Badge className="bg-amber-500 text-xs">Inactive</Badge>
              )}
            </DialogTitle>
            {/* Delete Event Button */}
            {isAdmin && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleDeleteEvent}
                data-testid="delete-event-btn"
                className="w-full sm:w-auto"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Event
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Dashboard Summary - Status-Based Counts */}
        {dashboard && (
          <div className="space-y-4 mb-4">
            {/* Date Selector and Auto-Refresh */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-slate-50 rounded-lg">
              <Label className="font-medium text-sm">View Date:</Label>
              <div className="flex items-center gap-2 flex-1">
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="flex-1 sm:w-[180px] sm:flex-none"
                />
                <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}>
                  Today
                </Button>
              </div>
              {/* Auto-Refresh Toggle */}
              <div className="flex items-center gap-2 border-l pl-2 sm:pl-4">
                <Button
                  variant={autoRefresh ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={autoRefresh ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
                  {autoRefresh ? 'Auto' : 'Manual'}
                </Button>
                <span className="text-xs text-slate-500 hidden sm:inline">
                  {autoRefresh ? `Every 10s` : 'Click to enable'}
                </span>
              </div>
            </div>

            {/* Official Status Counts - Based on permanent status */}
            <div>
              <p className="text-sm font-medium text-slate-600 mb-2">📊 Event Progress (Official Counts):</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <Card className="bg-emerald-500 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.status_counts?.takhmeen_done || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Takhmeen Done</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-500 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.status_counts?.payment_pending || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Payment Pending</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-500 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.status_counts?.payment_completed || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Payment Completed</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-500 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.status_counts?.pending_hof || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Pending HOF</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Today's Token Activity */}
            <div>
              <p className="text-sm font-medium text-slate-600 mb-2">📅 Today's Tokens ({selectedDate}):</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <Card className="bg-blue-500 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.today?.total_tokens || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Total Tokens</p>
                  </CardContent>
                </Card>
                <Card className="bg-indigo-400 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.today?.wajebaat_tokens || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Wajebaat</p>
                  </CardContent>
                </Card>
                <Card className="bg-teal-400 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.today?.fmb_tokens || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">FMB</p>
                  </CardContent>
                </Card>
                <Card className="bg-violet-400 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.today?.payment_tokens || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Payment</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Full Event Token Stats */}
            <div>
              <p className="text-sm font-medium text-slate-600 mb-2">📈 All-Time Token Stats:</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <Card className="bg-navy text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.full?.total_tokens || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Total Tokens</p>
                  </CardContent>
                </Card>
                <Card className="bg-indigo-600 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.full?.wajebaat_tokens || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Wajebaat</p>
                  </CardContent>
                </Card>
                <Card className="bg-teal-600 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.full?.fmb_tokens || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">FMB</p>
                  </CardContent>
                </Card>
                <Card className="bg-violet-600 text-white">
                  <CardContent className="p-2 sm:p-3 text-center">
                    <p className="text-xl sm:text-2xl font-bold">{dashboard.full?.payment_tokens || 0}</p>
                    <p className="text-[10px] sm:text-xs opacity-80">Payment</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Mohalla-wise Breakdown - CLICKABLE with Pending HOF */}
            {dashboard.mohalla_breakdown && dashboard.mohalla_breakdown.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">🏘️ Mohalla-wise Breakdown (Click to view details):</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {dashboard.mohalla_breakdown.map((mohalla) => (
                    <Card key={mohalla._id} className="border-l-4 border-l-navy">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-bold text-navy">{mohalla._id}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-2">
                        <div 
                          className="flex justify-between items-center p-2 bg-emerald-50 rounded cursor-pointer hover:bg-emerald-100"
                          onClick={() => fetchMohallaDrilldown(mohalla._id, 'takhmeen_done')}
                          data-testid={`mohalla-takhmeen-done-${mohalla._id}`}
                        >
                          <span className="text-sm text-emerald-700">Takhmeen Done</span>
                          <Badge className="bg-emerald-500">{mohalla.takhmeen_done || 0}</Badge>
                        </div>
                        <div 
                          className="flex justify-between items-center p-2 bg-orange-50 rounded cursor-pointer hover:bg-orange-100"
                          onClick={() => fetchMohallaDrilldown(mohalla._id, 'payment_pending')}
                          data-testid={`mohalla-payment-pending-${mohalla._id}`}
                        >
                          <span className="text-sm text-orange-700">Payment Pending</span>
                          <Badge className="bg-orange-500">{mohalla.payment_pending || 0}</Badge>
                        </div>
                        <div 
                          className="flex justify-between items-center p-2 bg-purple-50 rounded cursor-pointer hover:bg-purple-100"
                          onClick={() => fetchMohallaDrilldown(mohalla._id, 'payment_done')}
                          data-testid={`mohalla-payment-done-${mohalla._id}`}
                        >
                          <span className="text-sm text-purple-700">Payment Completed</span>
                          <Badge className="bg-purple-500">{mohalla.payment_completed || 0}</Badge>
                        </div>
                        {/* Pending HOF - HOFs without completed Takhmeen */}
                        <div 
                          className="flex justify-between items-center p-2 bg-red-50 rounded cursor-pointer hover:bg-red-100"
                          onClick={() => fetchMohallaDrilldown(mohalla._id, 'pending_hof')}
                          data-testid={`mohalla-pending-hof-${mohalla._id}`}
                        >
                          <span className="text-sm text-red-700">Pending HOF</span>
                          <Badge className="bg-red-500">{mohalla.pending_hof || 0}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Daily History - Simplified */}
            {dashboard.history && dashboard.history.length > 0 && (
              <details className="bg-slate-50 rounded-lg p-3">
                <summary className="cursor-pointer font-medium text-sm text-slate-700">📜 View Daily History ({dashboard.history.length} days)</summary>
                <div className="mt-3 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Tokens</TableHead>
                        <TableHead>Takhmeen</TableHead>
                        <TableHead>Payment</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboard.history.map((day) => (
                        <TableRow key={day._id} className={day._id === selectedDate ? 'bg-blue-50' : ''}>
                          <TableCell className="font-mono">{day._id}</TableCell>
                          <TableCell>{day.tokens_generated || 0}</TableCell>
                          <TableCell>{day.takhmeen_marked || 0}</TableCell>
                          <TableCell>{day.payment_marked || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </details>
            )}
          </div>
        )}

        {/* Mohalla Drilldown Dialog */}
        {showDrilldown && drilldownData && (
          <Dialog open={showDrilldown} onOpenChange={() => setShowDrilldown(false)}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-lg">
                  {drilldownData.mohalla} - {
                    drilldownData.filter_type === 'takhmeen_done' ? 'Takhmeen Done' :
                    drilldownData.filter_type === 'takhmeen_pending' ? 'Pending Tokens' :
                    drilldownData.filter_type === 'payment_done' ? 'Payment Done' :
                    drilldownData.filter_type === 'pending_hof' ? 'Pending HOF' :
                    'Payment Pending'
                  }
                  <Badge className="ml-2">{drilldownData.total} {drilldownData.filter_type === 'pending_hof' ? 'HOFs' : 'tokens'}</Badge>
                </DialogTitle>
              </DialogHeader>
              
              {/* Export buttons */}
              <div className="flex gap-2 mb-4">
                <Button 
                  onClick={() => handleExport('pdf')} 
                  size="sm" 
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                {user?.role === 'super_admin' && (
                  <Button 
                    onClick={() => handleExport('excel')} 
                    size="sm" 
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Download className="w-4 h-4 mr-1" /> Excel
                  </Button>
                )}
              </div>

              {/* Drilldown Table - Different columns for Pending HOF */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sr</TableHead>
                    <TableHead>ITS</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Mohalla</TableHead>
                    <TableHead>Sector</TableHead>
                    {drilldownData.filter_type === 'pending_hof' ? (
                      <>
                        <TableHead>Last Token Date</TableHead>
                        <TableHead>Status</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead>Token Type</TableHead>
                        <TableHead>Token #</TableHead>
                        <TableHead>Date</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drilldownData.tokens.map((token, idx) => (
                    <TableRow key={token.id || token.its_id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell className="font-mono">{token.its_id}</TableCell>
                      <TableCell>{token.full_name}</TableCell>
                      <TableCell>{token.mohalla}</TableCell>
                      <TableCell>{token.sector || '-'}</TableCell>
                      {drilldownData.filter_type === 'pending_hof' ? (
                        <>
                          <TableCell>{token.last_token_date || 'No token'}</TableCell>
                          <TableCell>
                            <Badge className="bg-red-500">{token.status}</Badge>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <Badge variant="outline">
                              {{"wajebaat": "Wajebaat", "fmb": "FMB", "payment": "Payment"}[token.token_type] || token.token_type || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-bold">{token.token_number || '-'}</TableCell>
                          <TableCell>{token.generation_date || '-'}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DialogContent>
          </Dialog>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="generate" disabled={event.is_completed} className="text-xs sm:text-sm px-1 sm:px-3">
              <Ticket className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">Generate</span>
              <span className="sm:hidden">Gen</span>
            </TabsTrigger>
            <TabsTrigger value="tokens" className="text-xs sm:text-sm px-1 sm:px-3">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">Today's Tokens</span>
              <span className="sm:hidden">Today</span>
            </TabsTrigger>
            <TabsTrigger value="pending" className="text-xs sm:text-sm px-1 sm:px-3">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">Pending HOF</span>
              <span className="sm:hidden">HOF</span>
            </TabsTrigger>
            <TabsTrigger value="payment_pending" className="text-xs sm:text-sm px-1 sm:px-3 relative">
              <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">Payment</span>
              <span className="sm:hidden">Pay</span>
              {dashboard?.status_counts?.payment_pending > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                  {dashboard?.status_counts?.payment_pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reports" className="text-xs sm:text-sm px-1 sm:px-3">
              <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">Reports</span>
              <span className="sm:hidden">Rep</span>
            </TabsTrigger>
          </TabsList>

          {/* Generate Token Tab */}
          <TabsContent value="generate" className="mt-4">
            {event.is_completed ? (
              <div className="text-center py-8 text-slate-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                <p>This event is completed. No new tokens can be generated.</p>
              </div>
            ) : (
              <TokenGenerationForm 
                eventId={event.id} 
                api={api} 
                onSuccess={() => { fetchDashboard(); }}
              />
            )}
          </TabsContent>

          {/* Today's Tokens Tab */}
          <TabsContent value="tokens" className="mt-4">
            <TokensListView
              tokens={tokens}
              loading={tokensLoading}
              filters={filters}
              setFilters={setFilters}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              selectedTokens={selectedTokens}
              setSelectedTokens={setSelectedTokens}
              onUpdateStatus={handleUpdateStatus}
              onBulkStatus={handleBulkStatus}
              onSendReminder={() => setShowReminderDialog(true)}
              onRefresh={() => fetchTokens('')}
              showStatusFilter={true}
              api={api}
              eventId={event.id}
              onPrintTokenList={handlePrintTokenList}
              onBluetoothPrint={handleBluetoothPrintAll}
              bluetoothConnecting={bluetoothConnecting}
              bluetoothPrinting={bluetoothPrinting}
              printerConnected={!!printerCharacteristic}
              onConnectPrinter={connectBluetoothPrinter}
              selectedDate={selectedDate}
            />
          </TabsContent>

          {/* Pending Tab - HOFs without tokens + Tokens with pending status */}
          <TabsContent value="pending" className="mt-4">
            <PendingHofView
              eventId={event.id}
              api={api}
              onGenerateToken={(family) => {
                setActiveTab('generate');
                // Pre-fill the search with the family
              }}
            />
          </TabsContent>

          {/* Payment Pending Tab - Members with completed Takhmeen but pending Payment */}
          <TabsContent value="payment_pending" className="mt-4">
            <PaymentPendingView
              eventId={event.id}
              api={api}
              onSuccess={() => fetchDashboard()}
            />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-4">
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg">Export Reports</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <Label className="form-label text-xs sm:text-sm">Date</Label>
                      <Input
                        type="date"
                        value={filters.date || ''}
                        onChange={(e) => setFilters({...filters, date: e.target.value})}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="form-label text-xs sm:text-sm">Token Type</Label>
                      <Select value={filters.token_type || 'all'} onValueChange={(v) => setFilters({...filters, token_type: v === 'all' ? '' : v})}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="All Types" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          {TOKEN_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="form-label text-xs sm:text-sm">Status</Label>
                      <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({...filters, status: v === 'all' ? '' : v})}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="All Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="takhmeen_done">Takhmeen Done</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="form-label text-xs sm:text-sm">Completion</Label>
                      <Select value={filters.completion_filter || 'all'} onValueChange={(v) => setFilters({...filters, completion_filter: v === 'all' ? '' : v})}>
                        <SelectTrigger data-testid="completion-filter-select" className="h-9"><SelectValue placeholder="All" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tokens</SelectItem>
                          <SelectItem value="not_generated">Not Generated</SelectItem>
                          <SelectItem value="takhmeen_not_done">Takhmeen Pending</SelectItem>
                          <SelectItem value="takhmeen_done">Takhmeen Done</SelectItem>
                          <SelectItem value="payment_pending">Payment Pending</SelectItem>
                          <SelectItem value="payment_done">Payment Done</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 sm:gap-3 flex-wrap">
                    <Button onClick={() => handleExport('pdf')} className="bg-red-600 hover:bg-red-700 flex-1 sm:flex-none text-xs sm:text-sm" data-testid="export-pdf-btn">
                      <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      PDF
                    </Button>
                    {user?.role === 'super_admin' && (
                      <Button onClick={() => handleExport('excel')} className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none text-xs sm:text-sm" data-testid="export-excel-btn">
                        <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        Excel
                      </Button>
                    )}
                    <Button onClick={handlePrintReport} variant="outline" className="flex-1 sm:flex-none text-xs sm:text-sm" data-testid="print-btn">
                      <Printer className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      Print
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Breakdown */}
              {dashboard && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm sm:text-base">By Token Type</CardTitle></CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Wajebaat</span>
                          <Badge>{dashboard.full?.wajebaat_tokens || 0}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>FMB</span>
                          <Badge>{dashboard.full?.fmb_tokens || 0}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Payment</span>
                          <Badge>{dashboard.full?.payment_tokens || 0}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm sm:text-base">By Status (Official)</CardTitle></CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-red-600 text-xs sm:text-sm">Pending HOF</span>
                          <Badge className="bg-red-500">{dashboard.status_counts?.pending_hof || 0}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-emerald-600 text-xs sm:text-sm">Takhmeen Done</span>
                          <Badge className="bg-emerald-500">{dashboard.status_counts?.takhmeen_done || 0}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-orange-600 text-xs sm:text-sm">Payment Pending</span>
                          <Badge className="bg-orange-500">{dashboard.status_counts?.payment_pending || 0}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-purple-600 text-xs sm:text-sm">Payment Completed</span>
                          <Badge className="bg-purple-500">{dashboard.status_counts?.payment_completed || 0}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Reminder Dialog */}
        {showReminderDialog && (
          <ReminderDialog
            api={api}
            tokenIds={selectedTokens}
            onClose={() => setShowReminderDialog(false)}
            onSuccess={() => { setShowReminderDialog(false); setSelectedTokens([]); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Token Generation Form - With VIP selection (VIP only for numbering, not displayed elsewhere)
function TokenGenerationForm({ eventId, api, onSuccess }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberStatus, setMemberStatus] = useState(null);
  const [tokenType, setTokenType] = useState('wajebaat');
  const [category, setCategory] = useState('general');
  const [vipNumber, setVipNumber] = useState('');
  const [availableVip, setAvailableVip] = useState([1,2,3,4,5,6,7,8,9,10]);
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Real-time search with debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await api.get(`/token-events/search-hof?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(response.data.families || []);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setSearching(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, api]);

  // Fetch member's event status when member is selected
  useEffect(() => {
    const fetchStatus = async () => {
      if (!selectedMember) {
        setMemberStatus(null);
        return;
      }
      const itsId = selectedMember.hof_its_id || selectedMember.its_id;
      try {
        const response = await api.get(`/token-events/${eventId}/member-status/${itsId}`);
        setMemberStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch member status:', error);
        setMemberStatus(null);
      }
    };
    fetchStatus();
  }, [selectedMember, eventId, api]);

  const fetchMemberStatus = async (itsId) => {
    try {
      const response = await api.get(`/token-events/${eventId}/member-status/${itsId}`);
      setMemberStatus(response.data);
    } catch (error) {
      setMemberStatus(null);
    }
  };

  // Fetch available VIP numbers when category changes to VIP
  useEffect(() => {
    if (category === 'vip') {
      fetchAvailableVip();
    }
  }, [category, tokenType]);

  const fetchAvailableVip = async () => {
    try {
      const response = await api.get(`/token-events/${eventId}/available-vip?token_type=${tokenType}`);
      setAvailableVip(response.data.available_vip_numbers || []);
    } catch (error) {
      console.error('Failed to fetch VIP numbers');
      setAvailableVip([1,2,3,4,5,6,7,8,9,10]);
    }
  };

  // Check if member already has a token for today
  const hasTokenForToday = (type) => {
    if (!memberStatus?.tokens) return false;
    const today = new Date().toISOString().split('T')[0];
    return memberStatus.tokens.some(t => t.token_type === type && t.generation_date === today);
  };

  // Get existing token number for today
  const getTodayTokenNumber = (type) => {
    if (!memberStatus?.tokens) return null;
    const today = new Date().toISOString().split('T')[0];
    const token = memberStatus.tokens.find(t => t.token_type === type && t.generation_date === today);
    return token?.token_number;
  };

  // Check if member has Takhmeen completed (either from status or from tokens)
  const hasTakhmeenCompleted = () => {
    // Check event_member_status first
    if (memberStatus?.status?.takhmeen_status === 'completed') return true;
    // Fallback: check if any token has takhmeen_done = true
    if (memberStatus?.tokens?.some(t => t.takhmeen_done)) return true;
    return false;
  };

  // Check if payment is already completed
  const hasPaymentCompleted = () => {
    if (memberStatus?.status?.payment_status === 'completed') return true;
    if (memberStatus?.tokens?.some(t => t.payment_done)) return true;
    return false;
  };

  // Generate token - NO blocking popup, just notification + auto print
  const handleGenerate = async () => {
    if (!selectedMember) {
      toast.error('Please select an HOF');
      return;
    }
    
    // Validate VIP number if VIP category selected
    if (category === 'vip' && !vipNumber) {
      toast.error('Please select a VIP number (1-10)');
      return;
    }

    // Check for duplicate token today
    if (hasTokenForToday(tokenType)) {
      const tokenNum = getTodayTokenNumber(tokenType);
      toast.error(`Already has ${tokenType} token #${tokenNum} for today!`);
      return;
    }

    // Validate payment token - Takhmeen must be completed
    if (tokenType === 'payment') {
      if (!hasTakhmeenCompleted()) {
        toast.error('Takhmeen must be completed before generating Payment token');
        return;
      }
      if (hasPaymentCompleted()) {
        toast.error('Payment is already completed for this member');
        return;
      }
    }

    setGenerating(true);
    try {
      const response = await api.post(`/token-events/${eventId}/generate`, {
        member_id: selectedMember.hof_id || selectedMember.id,
        its_id: selectedMember.hof_its_id || selectedMember.its_id,
        full_name: selectedMember.hof_name || selectedMember.full_name,
        mohalla: selectedMember.mohalla || '',
        sector: selectedMember.sector || '',
        mobile: selectedMember.mobile || selectedMember.hof_mobile || '',
        token_type: tokenType,
        category: category,
        vip_number: category === 'vip' ? parseInt(vipNumber) : null
      });
      
      const generatedToken = response.data.token;
      setLastGenerated(generatedToken);
      
      // Show success notification with auto-takhmeen indicator
      if (response.data.auto_takhmeen) {
        toast.success('Token Generated! Takhmeen auto-marked ✓', { duration: 4000 });
      } else {
        toast.success('Token Generated Successfully!', { duration: 3000 });
      }
      
      // Auto-open print modal/slip after short delay
      if (generatedToken) {
        setShowPrintModal(true);
      }
      
      // Reset form but keep token type
      setSelectedMember(null);
      setMemberStatus(null);
      setSearchQuery('');
      setSearchResults([]);
      setVipNumber('');
      onSuccess();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to generate token';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Token generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Bluetooth Printer State
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [bluetoothConnecting, setBluetoothConnecting] = useState(false);
  const [bluetoothPrinting, setBluetoothPrinting] = useState(false);
  const [printerCharacteristic, setPrinterCharacteristic] = useState(null);

  // Check if Web Bluetooth is available (Android Chrome)
  const isBluetoothAvailable = typeof navigator !== 'undefined' && navigator.bluetooth;

  // Connect to Bluetooth Printer - tries multiple approaches
  const connectBluetoothPrinter = async () => {
    if (!isBluetoothAvailable) {
      toast.error('Bluetooth not available. Use Chrome on Android.');
      return false;
    }

    try {
      setBluetoothConnecting(true);
      
      // Request device - accept all devices for maximum compatibility
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          // Common thermal printer services
          '000018f0-0000-1000-8000-00805f9b34fb',
          '49535343-fe7d-4ae5-8fa9-9fafd205e455',
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
          '0000ff00-0000-1000-8000-00805f9b34fb',
          '0000ffe0-0000-1000-8000-00805f9b34fb',
          '0000fee7-0000-1000-8000-00805f9b34fb',
          '00001101-0000-1000-8000-00805f9b34fb',
          '49535343-8841-43f4-a8d4-ecbe34729bb3',
          '49535343-1e4d-4bd9-ba61-23c647249616',
          '0000fff0-0000-1000-8000-00805f9b34fb',
        ]
      });

      setBluetoothDevice(device);
      toast.info(`Connecting to ${device.name || 'Printer'}...`);
      
      // Try to connect to GATT server
      let server;
      try {
        server = await device.gatt.connect();
      } catch (gattError) {
        console.error('GATT connect error:', gattError);
        toast.error('Cannot connect. Your printer may use Bluetooth Classic (not BLE). Try "Share to Print" option below.');
        setBluetoothConnecting(false);
        return false;
      }
      
      // Find writable characteristic
      const characteristic = await findPrinterCharacteristic(server);
      
      if (characteristic) {
        setPrinterCharacteristic(characteristic);
        toast.success('Printer connected!');
        
        device.addEventListener('gattserverdisconnected', () => {
          setBluetoothDevice(null);
          setPrinterCharacteristic(null);
          toast.info('Printer disconnected');
        });
        
        return true;
      } else {
        toast.error('Printer found but not BLE compatible. Use "Share to Print" option.');
        return false;
      }
    } catch (error) {
      console.error('Bluetooth error:', error);
      if (error.name === 'NotFoundError') {
        toast.info('No printer selected');
      } else if (error.message?.includes('User cancelled')) {
        toast.info('Selection cancelled');
      } else {
        toast.error('Connection failed. Your printer may not support BLE.');
      }
      return false;
    } finally {
      setBluetoothConnecting(false);
    }
  };

  // Find writable characteristic for printing
  const findPrinterCharacteristic = async (server) => {
    const serviceUUIDs = [
      '000018f0-0000-1000-8000-00805f9b34fb',
      '49535343-fe7d-4ae5-8fa9-9fafd205e455',
      'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
      '0000ff00-0000-1000-8000-00805f9b34fb',
      '0000ffe0-0000-1000-8000-00805f9b34fb',
      '0000fff0-0000-1000-8000-00805f9b34fb',
      '0000fee7-0000-1000-8000-00805f9b34fb',
    ];
    
    for (const serviceUUID of serviceUUIDs) {
      try {
        const service = await server.getPrimaryService(serviceUUID);
        const characteristics = await service.getCharacteristics();
        
        for (const char of characteristics) {
          const props = char.properties;
          if (props.write || props.writeWithoutResponse) {
            console.log('Found characteristic:', char.uuid);
            return char;
          }
        }
      } catch (e) {
        continue;
      }
    }
    
    // Fallback: enumerate all services
    try {
      const services = await server.getPrimaryServices();
      for (const service of services) {
        try {
          const characteristics = await service.getCharacteristics();
          for (const char of characteristics) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              return char;
            }
          }
        } catch (e) {
          continue;
        }
      }
    } catch (e) {
      console.log('Service enumeration failed');
    }
    
    return null;
  };

  // Generate ESC/POS commands for 58mm thermal printer (32 chars per line)
  const generateESCPOSCommands = (token) => {
    const encoder = new TextEncoder();
    const commands = [];
    
    const ESC = 0x1B;
    const GS = 0x1D;
    const LF = 0x0A;
    
    commands.push(ESC, 0x40); // Initialize
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(ESC, 0x45, 0x01); // Bold
    commands.push(GS, 0x21, 0x01); // Double height
    
    const eventName = (token.event_name || 'Token').substring(0, 20);
    commands.push(...encoder.encode(eventName));
    commands.push(LF);
    
    commands.push(GS, 0x21, 0x00);
    commands.push(...encoder.encode('================================'));
    commands.push(LF);
    
    commands.push(GS, 0x21, 0x11); // Large
    commands.push(...encoder.encode(`Token No #${token.token_number}`));
    commands.push(LF);
    
    commands.push(GS, 0x21, 0x00);
    commands.push(ESC, 0x45, 0x00);
    commands.push(...encoder.encode('--------------------------------'));
    commands.push(LF);
    
    commands.push(ESC, 0x61, 0x00); // Left
    
    const typeMap = {"wajebaat": "Wajebaat", "fmb": "FMB", "payment": "Payment"};
    
    // Type
    commands.push(...encoder.encode(`Type: ${typeMap[token.token_type] || token.token_type}`));
    commands.push(LF);
    
    // ITS
    commands.push(...encoder.encode(`ITS: ${token.its_id}`));
    commands.push(LF);
    
    // Full Name - can span multiple lines
    commands.push(...encoder.encode('Name:'));
    commands.push(LF);
    const fullName = token.full_name || '';
    if (fullName.length <= 32) {
      commands.push(...encoder.encode(fullName));
      commands.push(LF);
    } else {
      // Split name into multiple lines
      commands.push(...encoder.encode(fullName.substring(0, 32)));
      commands.push(LF);
      if (fullName.length > 32) {
        commands.push(...encoder.encode(fullName.substring(32, 64)));
        commands.push(LF);
      }
    }
    
    // Mohalla
    commands.push(...encoder.encode(`Mohalla: ${token.mohalla || '-'}`.substring(0, 32)));
    commands.push(LF);
    
    // Sector
    commands.push(...encoder.encode(`Sector: ${token.sector || '-'}`));
    commands.push(LF);
    
    // Date
    commands.push(...encoder.encode(`Date: ${token.generation_date}`));
    commands.push(LF);
    
    // Footer
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(...encoder.encode('--------------------------------'));
    commands.push(LF);
    commands.push(...encoder.encode('Please keep this token'));
    commands.push(LF);
    commands.push(...encoder.encode('for reference.'));
    commands.push(LF, LF, LF, LF);
    
    return new Uint8Array(commands);
  };

  // Print via Bluetooth BLE
  const handleBluetoothPrint = async () => {
    if (!lastGenerated) {
      toast.error('No token to print');
      return;
    }
    
    try {
      setBluetoothPrinting(true);
      
      let characteristic = printerCharacteristic;
      
      if (!characteristic || !bluetoothDevice?.gatt?.connected) {
        const connected = await connectBluetoothPrinter();
        if (!connected) {
          setBluetoothPrinting(false);
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 300));
        characteristic = printerCharacteristic;
      }
      
      if (!characteristic) {
        toast.error('Printer not ready. Use Share to Print instead.');
        return;
      }

      toast.info('Sending to printer...');
      
      const printData = generateESCPOSCommands(lastGenerated);
      const chunkSize = 20;
      
      for (let i = 0; i < printData.length; i += chunkSize) {
        const chunk = printData.slice(i, Math.min(i + chunkSize, printData.length));
        try {
          if (characteristic.properties.writeWithoutResponse) {
            await characteristic.writeValueWithoutResponse(chunk);
          } else {
            await characteristic.writeValue(chunk);
          }
        } catch (writeError) {
          throw writeError;
        }
        await new Promise(resolve => setTimeout(resolve, 30));
      }
      
      toast.success('Printed!');
      setShowPrintModal(false);
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Print failed. Try Share to Print option.');
      setPrinterCharacteristic(null);
    } finally {
      setBluetoothPrinting(false);
    }
  };

  // Share to Print - Works with Bluetooth Classic printers via Android print service
  const handleShareToPrint = async () => {
    if (!lastGenerated) return;
    
    const typeMap = {"wajebaat": "Wajebaat", "fmb": "FMB", "payment": "Payment"};
    
    // Create print content
    const printContent = `
================================
    ${lastGenerated.event_name || 'Token Event'}
================================

    Token No #${lastGenerated.token_number}

--------------------------------
Type: ${typeMap[lastGenerated.token_type] || lastGenerated.token_type}
ITS: ${lastGenerated.its_id}
Name: ${lastGenerated.full_name}
Mohalla: ${lastGenerated.mohalla}
Sector: ${lastGenerated.sector || '-'}
Date: ${lastGenerated.generation_date}
--------------------------------
Please keep this token
    for reference.
================================
`.trim();

    // Try Web Share API first (Android)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Token No #${lastGenerated.token_number}`,
          text: printContent,
        });
        toast.success('Shared! Select your printer app.');
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to copy
          await navigator.clipboard.writeText(printContent);
          toast.success('Copied! Paste in your printer app.');
        }
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(printContent);
        toast.success('Copied to clipboard! Paste in printer app.');
      } catch (e) {
        toast.error('Could not copy. Please use browser print.');
      }
    }
  };

  // Disconnect printer
  const disconnectPrinter = () => {
    if (bluetoothDevice?.gatt?.connected) {
      bluetoothDevice.gatt.disconnect();
    }
    setBluetoothDevice(null);
    setPrinterCharacteristic(null);
    toast.info('Disconnected');
  };

  // 58mm Print Slip - Browser print
  const handlePrint = () => {
    if (!lastGenerated) return;
    
    const tokenTypeDisplay = {"wajebaat": "Wajebaat", "fmb": "FMB", "payment": "Payment"}[lastGenerated.token_type] || lastGenerated.token_type;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Token Slip</title>
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          @page { size: 58mm auto; margin: 2mm; }
          @media print { 
            body { width: 54mm; } 
            .no-print { display: none; }
          }
          body { 
            font-family: 'Courier New', Courier, monospace; 
            width: 54mm; 
            max-width: 58mm;
            margin: 0 auto; 
            padding: 2mm;
            font-size: 11px;
            line-height: 1.3;
            background: white;
          }
          .header { text-align: center; font-weight: bold; font-size: 11px; margin-bottom: 2mm; padding-bottom: 2mm; border-bottom: 1px dashed #000; }
          .token-num { text-align: center; font-size: 20px; font-weight: bold; margin: 3mm 0; }
          .divider { border-top: 1px dashed #000; margin: 2mm 0; }
          .row { display: flex; justify-content: space-between; margin: 1mm 0; font-size: 10px; }
          .label { font-weight: bold; flex-shrink: 0; }
          .value { text-align: right; }
          .name-row { margin: 1mm 0; font-size: 10px; }
          .name-label { font-weight: bold; }
          .name-value { word-break: break-word; overflow-wrap: break-word; line-height: 1.4; font-weight: 500; }
          .footer { text-align: center; font-size: 8px; margin-top: 2mm; padding-top: 2mm; border-top: 1px dashed #000; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">${lastGenerated.event_name || 'Token Event'}</div>
        <div class="token-num">Token No #${lastGenerated.token_number}</div>
        <div class="divider"></div>
        <div class="row"><span class="label">Type:</span><span class="value">${tokenTypeDisplay}</span></div>
        <div class="row"><span class="label">ITS:</span><span class="value">${lastGenerated.its_id}</span></div>
        <div class="row"><span class="label">Sector:</span><span class="value">${lastGenerated.sector || '-'}</span></div>
        <div class="name-row">
          <span class="name-label">Name:</span>
          <div class="name-value">${lastGenerated.full_name || ''}</div>
        </div>
        <div class="row"><span class="label">Mohalla:</span><span class="value">${lastGenerated.mohalla || ''}</span></div>
        <div class="row"><span class="label">Date:</span><span class="value">${lastGenerated.generation_date}</span></div>
        <div class="footer">Please keep this token for reference.</div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=250,height=400');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => printWindow.print(), 100);
      };
    } else {
      toast.error('Popup blocked. Please allow popups for printing.');
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
      {/* Left: Search & Select HOF */}
      <Card>
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-sm sm:text-base flex items-center gap-2">
            <Search className="w-4 h-4" />
            Search HOF (Head of Family)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          <div className="relative">
            <Input
              placeholder="Type ITS, HOF Name, or Mobile to search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            {searching && (
              <RefreshCw className="w-4 h-4 animate-spin absolute right-3 top-3 text-slate-400" />
            )}
          </div>
          
          {searchQuery.length > 0 && searchQuery.length < 2 && (
            <p className="text-xs text-slate-500">Type at least 2 characters to search...</p>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              {searchResults.map((family) => (
                <div
                  key={family.id}
                  onClick={() => setSelectedMember(family)}
                  className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-slate-50 ${
                    selectedMember?.id === family.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <p className="font-medium">{family.hof_name}</p>
                  <p className="text-xs text-slate-500">
                    ITS: {family.hof_its_id} | {family.mohalla} - {family.sector}
                  </p>
                  {family.mobile && <p className="text-xs text-slate-500">📱 {family.mobile}</p>}
                </div>
              ))}
            </div>
          )}
          
          {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
            <p className="text-sm text-slate-500 text-center py-4">No HOF found matching "{searchQuery}"</p>
          )}

          {/* Selected HOF with Status */}
          {selectedMember && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
              <p className="text-sm font-medium text-emerald-800">Selected HOF:</p>
              <p className="font-semibold">{selectedMember.hof_name || selectedMember.full_name}</p>
              <p className="text-xs text-emerald-600">
                ITS: {selectedMember.hof_its_id || selectedMember.its_id} | {selectedMember.mohalla} - {selectedMember.sector}
              </p>
              
              {/* Show member status - from status record OR inferred from tokens */}
              {(memberStatus?.status || memberStatus?.tokens?.length > 0) && (
                <div className="mt-2 pt-2 border-t border-emerald-200">
                  <p className="text-xs font-medium text-slate-600 mb-1">Event Status:</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge className={hasTakhmeenCompleted() ? 'bg-emerald-500' : 
                                     memberStatus?.status?.takhmeen_status === 'token_generated' ? 'bg-blue-500' : 
                                     memberStatus?.tokens?.length > 0 ? 'bg-blue-500' : 'bg-slate-400'}>
                      Takhmeen: {hasTakhmeenCompleted() ? 'Done' : 
                                memberStatus?.tokens?.length > 0 ? 'Token Generated' : 'Not Started'}
                    </Badge>
                    <Badge className={hasPaymentCompleted() ? 'bg-purple-500' : 
                                     hasTakhmeenCompleted() ? 'bg-orange-500' : 'bg-slate-400'}>
                      Payment: {hasPaymentCompleted() ? 'Done' : 
                               hasTakhmeenCompleted() ? 'Pending' : 'Not Required'}
                    </Badge>
                  </div>
                </div>
              )}
              
              {/* Show existing tokens for today */}
              {memberStatus?.tokens && memberStatus.tokens.length > 0 && (
                <div className="mt-2 pt-2 border-t border-emerald-200">
                  <p className="text-xs font-medium text-slate-600 mb-1">Tokens Generated:</p>
                  <div className="space-y-1">
                    {memberStatus.tokens.filter(t => t.generation_date === new Date().toISOString().split('T')[0]).map(t => (
                      <div key={t.id} className="flex items-center gap-2 text-xs">
                        <Badge variant="outline" className="text-blue-600 border-blue-300">
                          {t.token_type.toUpperCase()} #{t.token_number}
                        </Badge>
                        <span className="text-slate-500">Today</span>
                        {t.takhmeen_done && <Badge className="bg-emerald-500 text-xs">Takhmeen ✓</Badge>}
                        {t.payment_done && <Badge className="bg-purple-500 text-xs">Payment ✓</Badge>}
                      </div>
                    ))}
                    {memberStatus.tokens.filter(t => t.generation_date !== new Date().toISOString().split('T')[0]).length > 0 && (
                      <p className="text-xs text-slate-500">
                        + {memberStatus.tokens.filter(t => t.generation_date !== new Date().toISOString().split('T')[0]).length} token(s) from previous days
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Warning if trying to generate payment without takhmeen */}
              {tokenType === 'payment' && !hasTakhmeenCompleted() && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                  ⚠️ Cannot generate Payment token - Takhmeen not completed
                </div>
              )}
              
              {/* Already has token for today warning */}
              {hasTokenForToday(tokenType) && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                  ⚠️ Already has {tokenType.toUpperCase()} token #{getTodayTokenNumber(tokenType)} for today
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Right: Token Configuration - With VIP for numbering only */}
      <Card>
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-sm sm:text-base flex items-center gap-2">
            <Ticket className="w-4 h-4" />
            Token Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          <div>
            <Label className="form-label">Token Type *</Label>
            <Select value={tokenType} onValueChange={setTokenType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TOKEN_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* VIP/General Selection - Only for numbering logic */}
          <div>
            <Label className="form-label">Token Number Range *</Label>
            <Select value={category} onValueChange={(v) => { setCategory(v); setVipNumber(''); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General (11 onwards)</SelectItem>
                <SelectItem value="vip">VIP (1-10)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              VIP/General is only for token numbering. Not shown in reports.
            </p>
          </div>

          {/* VIP Number Selection */}
          {category === 'vip' && (
            <div>
              <Label className="form-label">Select VIP Number (1-10) *</Label>
              <Select value={vipNumber} onValueChange={setVipNumber}>
                <SelectTrigger>
                  <SelectValue placeholder="Select VIP number" />
                </SelectTrigger>
                <SelectContent>
                  {availableVip.length === 0 ? (
                    <SelectItem value="" disabled>All VIP numbers used</SelectItem>
                  ) : (
                    availableVip.map(n => (
                      <SelectItem key={n} value={n.toString()}>Token #{n}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Available: {availableVip.join(', ') || 'None'}
              </p>
            </div>
          )}

          <Button 
            onClick={handleGenerate} 
            disabled={generating || !selectedMember || (category === 'vip' && !vipNumber) || 
                     hasTokenForToday(tokenType) ||
                     (tokenType === 'payment' && !hasTakhmeenCompleted())}
            className="w-full bg-navy hover:bg-navy-light"
            data-testid="generate-token-btn"
          >
            {generating ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Generating...</>
            ) : hasTokenForToday(tokenType) ? (
              <><AlertCircle className="w-4 h-4 mr-2" />Already Has Token Today</>
            ) : tokenType === 'payment' && !hasTakhmeenCompleted() ? (
              <><AlertCircle className="w-4 h-4 mr-2" />Takhmeen Required First</>
            ) : (
              <><Ticket className="w-4 h-4 mr-2" />Generate Token</>
            )}
          </Button>

          {/* Last Generated Token - With Print Button */}
          {lastGenerated && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium text-blue-800 mb-2">Last Generated:</p>
              <div className="text-sm space-y-1">
                <p><strong>Token #:</strong> {lastGenerated.token_number}</p>
                <p><strong>Type:</strong> {{"wajebaat": "Wajebaat Takhmeen", "fmb": "FMB Takhmeen", "payment": "Payment"}[lastGenerated.token_type] || lastGenerated.token_type}</p>
                <p><strong>Name:</strong> {lastGenerated.full_name}</p>
              </div>
              <Button onClick={() => setShowPrintModal(true)} className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700" data-testid="print-slip-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print Token Slip
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Print Modal - Android Chrome Optimized */}
      {showPrintModal && lastGenerated && (
        <Dialog open={showPrintModal} onOpenChange={setShowPrintModal}>
          <DialogContent className="max-w-sm mx-2">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Printer className="w-5 h-5" />
                Print Token Slip
              </DialogTitle>
            </DialogHeader>
            
            {/* Token Slip Preview - 58mm optimized */}
            <div className="border border-slate-200 rounded-lg p-3 bg-white font-mono text-sm" id="token-slip-preview">
              <div className="text-center font-bold text-base mb-2 pb-2 border-b border-dashed border-slate-400">
                {lastGenerated.event_name || 'Token Event'}
              </div>
              <div className="text-center text-2xl font-bold my-2 text-navy">
                Token No #{lastGenerated.token_number}
              </div>
              <div className="border-t border-dashed border-slate-400 pt-2 space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Type:</span> <span className="font-medium">{{"wajebaat": "Wajebaat", "fmb": "FMB", "payment": "Payment"}[lastGenerated.token_type]}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">ITS:</span> <span className="font-mono">{lastGenerated.its_id}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Sector:</span> <span className="font-medium">{lastGenerated.sector || '-'}</span></div>
                <div>
                  <span className="text-slate-500">Name:</span>
                  <div className="font-medium mt-0.5" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{lastGenerated.full_name}</div>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">Mohalla:</span> <span>{lastGenerated.mohalla}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Date:</span> <span>{lastGenerated.generation_date}</span></div>
              </div>
              <div className="text-center text-xs mt-2 pt-2 border-t border-dashed border-slate-400 text-slate-400">
                Please keep this token for reference.
              </div>
            </div>
            
            {/* Print Options */}
            <div className="space-y-3 mt-3">
              
              {/* Bluetooth Print - For BLE printers */}
              {isBluetoothAvailable && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${printerCharacteristic ? 'bg-green-500' : 'bg-slate-300'}`} />
                      <span className="text-sm text-blue-700">
                        {printerCharacteristic ? 'Printer Ready' : 'Bluetooth Printer'}
                      </span>
                    </div>
                    {printerCharacteristic && (
                      <Button variant="ghost" size="sm" onClick={disconnectPrinter} className="h-6 text-xs text-red-600 px-2">
                        Disconnect
                      </Button>
                    )}
                  </div>
                  {printerCharacteristic ? (
                    <Button 
                      onClick={handleBluetoothPrint} 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={bluetoothPrinting}
                    >
                      {bluetoothPrinting ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Printing...</>
                      ) : (
                        <><Printer className="w-4 h-4 mr-2" />Print to Bluetooth</>
                      )}
                    </Button>
                  ) : (
                    <Button 
                      onClick={connectBluetoothPrinter} 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={bluetoothConnecting}
                    >
                      {bluetoothConnecting ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Connecting...</>
                      ) : (
                        'Connect Bluetooth Printer'
                      )}
                    </Button>
                  )}
                </div>
              )}
              
              {/* Browser Print */}
              <div className="flex gap-2">
                <Button onClick={handlePrint} variant="outline" className="flex-1">
                  <Printer className="w-4 h-4 mr-2" />
                  Browser Print
                </Button>
                <Button variant="outline" onClick={() => setShowPrintModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Tokens List View Component
function TokensListView({ 
  tokens, loading, filters, setFilters, page, setPage, totalPages,
  selectedTokens, setSelectedTokens, onUpdateStatus, onBulkStatus, onSendReminder,
  onRefresh, showStatusFilter, onDeleteToken, eventId, api, onPrintTokenList,
  onBluetoothPrint, bluetoothConnecting, bluetoothPrinting, printerConnected, onConnectPrinter,
  selectedDate
}) {
  const [deleting, setDeleting] = useState(null);
  
  // Format date for display
  const displayDate = selectedDate ? new Date(selectedDate).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }) : 'Today';

  const toggleToken = (tokenId) => {
    setSelectedTokens(prev => 
      prev.includes(tokenId) 
        ? prev.filter(id => id !== tokenId)
        : [...prev, tokenId]
    );
  };

  const toggleAll = () => {
    if (selectedTokens.length === tokens.length && tokens.length > 0) {
      setSelectedTokens([]);
    } else {
      setSelectedTokens(tokens.map(t => t.id));
    }
  };

  // Delete Token Handler
  const handleDeleteToken = async (tokenId, tokenNumber, hofName) => {
    if (!window.confirm(`Are you sure you want to delete Token #${tokenNumber} for ${hofName}? This action cannot be undone.`)) {
      return;
    }
    setDeleting(tokenId);
    try {
      await api.delete(`/tokens/${tokenId}`);
      toast.success(`Token #${tokenNumber} deleted successfully`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete token');
    } finally {
      setDeleting(null);
    }
  };

  // Update Takhmeen/Payment Status
  const handleTwoStepUpdate = async (tokenId, field, value) => {
    try {
      const payload = { [field]: value };
      await api.put(`/tokens/${tokenId}/status`, payload);
      toast.success(`${field === 'takhmeen_done' ? 'Takhmeen' : 'Payment'} status updated`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-4">
      {/* Date Header */}
      <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-blue-700">Showing: {displayDate}</span>
        </div>
        <Badge variant="outline" className="bg-white">
          {tokens.length} token{tokens.length !== 1 ? 's' : ''}
        </Badge>
      </div>
      
      {/* Filters & Refresh */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 sm:items-end">
        <div className="flex-1 min-w-0 sm:min-w-[200px]">
          <Input
            placeholder="Search ITS, Name, Mobile..."
            value={filters.search || ''}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
          />
        </div>
        <div className="flex gap-2">
          <Select value={filters.token_type || 'all'} onValueChange={(v) => setFilters({...filters, token_type: v === 'all' ? '' : v})}>
            <SelectTrigger className="w-[120px] sm:w-[150px]"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TOKEN_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {showStatusFilter && (
            <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({...filters, status: v === 'all' ? '' : v})}>
              <SelectTrigger className="w-[100px] sm:w-[130px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={onRefresh} size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {/* Bluetooth Print Button - Primary */}
          <Button 
            onClick={onBluetoothPrint}
            disabled={bluetoothConnecting || bluetoothPrinting || tokens.length === 0}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="bluetooth-print-btn"
          >
            {bluetoothPrinting ? (
              <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Printer className="w-4 h-4 mr-1" />
            )}
            <span className="hidden sm:inline">
              {bluetoothPrinting ? 'Printing...' : printerConnected ? 'Print Today' : 'BT Print'}
            </span>
            <span className="sm:hidden">
              {bluetoothPrinting ? '...' : 'BT'}
            </span>
            {printerConnected && <span className="ml-1 w-2 h-2 bg-green-400 rounded-full"></span>}
          </Button>
          {/* Browser Print Fallback */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="px-2">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onPrintTokenList} data-testid="browser-print-btn">
                <FileText className="w-4 h-4 mr-2" />
                Browser Print (Today)
              </DropdownMenuItem>
              {!printerConnected && (
                <DropdownMenuItem onClick={onConnectPrinter} disabled={bluetoothConnecting}>
                  <Printer className="w-4 h-4 mr-2" />
                  {bluetoothConnecting ? 'Connecting...' : 'Connect Printer'}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedTokens.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-blue-50 rounded-lg items-center">
          <span className="text-sm font-medium">{selectedTokens.length} selected</span>
          <Button size="sm" onClick={() => onBulkStatus('completed')} className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm">
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />Mark Done
          </Button>
          <Button size="sm" onClick={onSendReminder} variant="outline" className="text-xs sm:text-sm">
            <Send className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />Remind
          </Button>
        </div>
      )}

      {/* Table - No VIP/Category display */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin text-navy" />
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Ticket className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>No tokens found</p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {tokens.map((token) => (
              <Card key={token.id} className="border shadow-sm">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={selectedTokens.includes(token.id)}
                        onCheckedChange={() => toggleToken(token.id)}
                      />
                      <span className="font-mono font-bold text-lg text-navy">#{token.token_number}</span>
                      <Badge variant="outline" className="text-xs">
                        {{"wajebaat": "Waj", "fmb": "FMB", "payment": "Pay"}[token.token_type] || token.token_type}
                      </Badge>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`token-actions-mobile-${token.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!token.takhmeen_done && (
                          <DropdownMenuItem onClick={() => handleTwoStepUpdate(token.id, 'takhmeen_done', true)}>
                            <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
                            Mark Takhmeen Done
                          </DropdownMenuItem>
                        )}
                        {token.takhmeen_done && !token.payment_done && (
                          <DropdownMenuItem onClick={() => handleTwoStepUpdate(token.id, 'payment_done', true)}>
                            <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                            Mark Payment Done
                          </DropdownMenuItem>
                        )}
                        {token.takhmeen_done && (
                          <DropdownMenuItem onClick={() => handleTwoStepUpdate(token.id, 'takhmeen_done', false)}>
                            <Clock className="w-4 h-4 mr-2 text-amber-600" />
                            Reset Takhmeen
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDeleteToken(token.id, token.token_number, token.full_name)}
                          className="text-red-600"
                          disabled={deleting === token.id}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {deleting === token.id ? 'Deleting...' : 'Delete Token'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium whitespace-normal break-words">{token.full_name}</p>
                    <p className="text-slate-500 text-xs">ITS: {token.its_id} | {token.mohalla}</p>
                    <p className="text-slate-400 text-xs">{token.generation_date}</p>
                  </div>
                  <div className="flex gap-4 mt-3 pt-2 border-t">
                    <label className="flex items-center gap-2 text-xs">
                      <Checkbox 
                        checked={token.takhmeen_done || false}
                        onCheckedChange={(checked) => handleTwoStepUpdate(token.id, 'takhmeen_done', checked)}
                      />
                      <span className={token.takhmeen_done ? 'text-blue-600 font-medium' : 'text-slate-500'}>Takhmeen</span>
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <Checkbox 
                        checked={token.payment_done || false}
                        onCheckedChange={(checked) => handleTwoStepUpdate(token.id, 'payment_done', checked)}
                        disabled={!token.takhmeen_done}
                      />
                      <span className={token.payment_done ? 'text-emerald-600 font-medium' : 'text-slate-500'}>Payment</span>
                    </label>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox 
                      checked={selectedTokens.length === tokens.length && tokens.length > 0}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead>Token #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>ITS</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Mohalla</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-center">Takhmeen</TableHead>
                  <TableHead className="text-center">Payment</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map((token) => (
                  <TableRow key={token.id}>
                    <TableCell>
                      <Checkbox 
                        checked={selectedTokens.includes(token.id)}
                        onCheckedChange={() => toggleToken(token.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="font-mono font-bold">{token.token_number}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {{"wajebaat": "Wajebaat", "fmb": "FMB", "payment": "Payment"}[token.token_type] || token.token_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">{token.its_id}</TableCell>
                    <TableCell className="min-w-[180px] max-w-[220px]">
                      <div className="whitespace-normal break-words leading-tight">
                        {token.full_name}
                      </div>
                    </TableCell>
                    <TableCell>{token.mohalla}</TableCell>
                    <TableCell>{token.sector || '-'}</TableCell>
                    <TableCell>{token.generation_date}</TableCell>
                    {/* Takhmeen Done Checkbox */}
                    <TableCell className="text-center">
                      <Checkbox 
                        checked={token.takhmeen_done || false}
                        onCheckedChange={(checked) => handleTwoStepUpdate(token.id, 'takhmeen_done', checked)}
                        data-testid={`takhmeen-checkbox-${token.id}`}
                      />
                    </TableCell>
                    {/* Payment Done Checkbox */}
                    <TableCell className="text-center">
                      <Checkbox 
                        checked={token.payment_done || false}
                        onCheckedChange={(checked) => handleTwoStepUpdate(token.id, 'payment_done', checked)}
                        disabled={!token.takhmeen_done}
                        data-testid={`payment-checkbox-${token.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`token-actions-${token.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!token.takhmeen_done && (
                            <DropdownMenuItem onClick={() => handleTwoStepUpdate(token.id, 'takhmeen_done', true)}>
                              <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
                              Mark Takhmeen Done
                            </DropdownMenuItem>
                          )}
                          {token.takhmeen_done && !token.payment_done && (
                            <DropdownMenuItem onClick={() => handleTwoStepUpdate(token.id, 'payment_done', true)}>
                              <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                              Mark Payment Done
                            </DropdownMenuItem>
                          )}
                          {token.takhmeen_done && (
                            <DropdownMenuItem onClick={() => handleTwoStepUpdate(token.id, 'takhmeen_done', false)}>
                              <Clock className="w-4 h-4 mr-2 text-amber-600" />
                              Reset Takhmeen
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteToken(token.id, token.token_number, token.full_name)}
                            className="text-red-600"
                            disabled={deleting === token.id}
                            data-testid={`delete-token-${token.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {deleting === token.id ? 'Deleting...' : 'Delete Token'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-sm">Page {page} of {totalPages}</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

// Reminder Dialog
function ReminderDialog({ api, tokenIds, onClose, onSuccess }) {
  const [message, setMessage] = useState(
    `Dear {name},\n\nThis is a reminder for your pending token #{token_number} for {event_name}.\n\nPlease complete the process at your earliest convenience.\n\nThank you.`
  );
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const response = await api.post('/tokens/send-reminder', {
        token_ids: tokenIds,
        message: message
      });
      toast.success(`Reminders sent: ${response.data.sent} success, ${response.data.failed} failed`);
      onSuccess();
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Send WhatsApp Reminder
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Sending reminder to <strong>{tokenIds.length}</strong> pending tokens
          </p>
          
          <div>
            <Label className="form-label">Message Template</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder="Enter reminder message..."
            />
            <p className="text-xs text-slate-500 mt-1">
              Variables: {'{name}'}, {'{token_number}'}, {'{event_name}'}, {'{token_type}'}, {'{its_id}'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSend} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
            {sending ? 'Sending...' : `Send to ${tokenIds.length} Members`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Pending HOF View - Shows HOFs who haven't generated tokens yet
function PendingHofView({ eventId, api, onGenerateToken }) {
  const [pendingHofs, setPendingHofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenType, setTokenType] = useState('wajebaat');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [existingCount, setExistingCount] = useState(0);

  const fetchPendingHofs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token_type: tokenType,
        page: page.toString(),
        limit: '50'
      });
      if (search) params.append('search', search);
      
      const response = await api.get(`/token-events/${eventId}/pending-hof?${params}`);
      setPendingHofs(response.data.families || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
      setExistingCount(response.data.existing_tokens_count || 0);
    } catch (error) {
      console.error('Failed to fetch pending HOFs:', error);
      toast.error('Failed to load pending HOFs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingHofs();
  }, [tokenType, page, eventId]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        fetchPendingHofs();
      } else {
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{total}</p>
            <p className="text-xs text-amber-700">HOFs Without Token</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-emerald-600">{existingCount}</p>
            <p className="text-xs text-emerald-700">Tokens Generated</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{total + existingCount}</p>
            <p className="text-xs text-blue-700">Total HOFs</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search HOF Name, ITS, Mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={tokenType} onValueChange={setTokenType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TOKEN_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchPendingHofs} size="sm">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin text-navy" />
        </div>
      ) : pendingHofs.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
          <p className="font-medium">All HOFs have generated tokens!</p>
          <p className="text-sm">No pending HOFs for {tokenType} tokens</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sr</TableHead>
                <TableHead>HOF Name</TableHead>
                <TableHead>ITS ID</TableHead>
                <TableHead>Mohalla</TableHead>
                <TableHead>Sector</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingHofs.map((family, idx) => (
                <TableRow key={family.id}>
                  <TableCell>{(page - 1) * 50 + idx + 1}</TableCell>
                  <TableCell className="font-medium">{family.hof_name}</TableCell>
                  <TableCell className="font-mono">{family.hof_its_id}</TableCell>
                  <TableCell>{family.mohalla || '-'}</TableCell>
                  <TableCell>{family.sector || '-'}</TableCell>
                  <TableCell>{family.mobile || '-'}</TableCell>
                  <TableCell>
                    <Badge className="bg-amber-500">Not Generated</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-sm">Page {page} of {totalPages} ({total} HOFs)</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}


// Payment Pending View - Members with completed Takhmeen but pending Payment
function PaymentPendingView({ eventId, api, onSuccess }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mohallaFilter, setMohallaFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [isVip, setIsVip] = useState(false);
  const [vipNumber, setVipNumber] = useState(1);
  const [availableVipNumbers, setAvailableVipNumbers] = useState([1,2,3,4,5,6,7,8,9,10]);

  const fetchPaymentPending = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      if (search) params.append('search', search);
      if (mohallaFilter) params.append('mohalla', mohallaFilter);
      
      const response = await api.get(`/token-events/${eventId}/payment-pending?${params}`);
      setMembers(response.data.members || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch payment pending members:', error);
      toast.error('Failed to load payment pending members');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVip = async () => {
    try {
      const response = await api.get(`/token-events/${eventId}/available-vip?token_type=payment`);
      setAvailableVipNumbers(response.data.available_vip_numbers || []);
    } catch (error) {
      console.error('Failed to fetch available VIP numbers:', error);
    }
  };

  const generatePaymentToken = async (member) => {
    setGeneratingFor(member.its_id);
    try {
      await api.post(`/token-events/${eventId}/generate`, {
        member_id: member.its_id,
        its_id: member.its_id,
        full_name: member.full_name,
        mohalla: member.mohalla,
        sector: member.sector || '',
        mobile: member.mobile || '',
        token_type: 'payment',
        category: isVip ? 'vip' : 'general',
        vip_number: isVip ? vipNumber : null
      });
      toast.success(`Payment token generated for ${member.full_name}`);
      fetchPaymentPending();
      fetchAvailableVip();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate payment token');
    } finally {
      setGeneratingFor(null);
    }
  };

  useEffect(() => {
    fetchPaymentPending();
    fetchAvailableVip();
  }, [page, eventId]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        fetchPaymentPending();
      } else {
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, mohallaFilter]);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <Card className="bg-orange-50 border-orange-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-2xl font-bold text-orange-600">{total}</p>
                <p className="text-sm text-orange-700">Members Pending Payment</p>
              </div>
            </div>
            <div className="text-sm text-orange-600">
              Takhmeen Done, Payment Pending
            </div>
          </div>
        </CardContent>
      </Card>

      {/* VIP Token Option */}
      <Card className="border-amber-300 bg-amber-50">
        <CardContent className="p-3">
          <div className="flex items-center gap-4 flex-wrap">
            <Label className="flex items-center gap-2">
              <Checkbox 
                checked={isVip} 
                onCheckedChange={setIsVip}
              />
              <Crown className="w-4 h-4 text-amber-500" />
              Generate as VIP Token
            </Label>
            {isVip && (
              <Select value={vipNumber.toString()} onValueChange={(v) => setVipNumber(parseInt(v))}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="VIP #" />
                </SelectTrigger>
                <SelectContent>
                  {availableVipNumbers.map(num => (
                    <SelectItem key={num} value={num.toString()}>VIP #{num}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <span className="text-xs text-amber-600">
              Available VIP: {availableVipNumbers.join(', ') || 'None'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search Name, ITS..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={mohallaFilter || "all"} onValueChange={(v) => setMohallaFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Mohallas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Mohallas</SelectItem>
            <SelectItem value="QUTBI MOHALLA">Qutbi Mohalla</SelectItem>
            <SelectItem value="NAJMI MOHALLA">Najmi Mohalla</SelectItem>
            <SelectItem value="FAKHRI MOHALLA">Fakhri Mohalla</SelectItem>
            <SelectItem value="BURHANI MOHALLA">Burhani Mohalla</SelectItem>
            <SelectItem value="HAKIMI MOHALLA">Hakimi Mohalla</SelectItem>
            <SelectItem value="EZZI MOHALLA">Ezzi Mohalla</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchPaymentPending} size="sm">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin text-navy" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
          <p className="font-medium">No payment pending members!</p>
          <p className="text-sm">All members with completed Takhmeen have either made payment or are not in queue</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sr</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>ITS ID</TableHead>
                <TableHead>Mohalla</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Payment Token</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member, idx) => (
                <TableRow key={member.its_id}>
                  <TableCell>{(page - 1) * 50 + idx + 1}</TableCell>
                  <TableCell className="font-medium">{member.full_name}</TableCell>
                  <TableCell className="font-mono text-sm">{member.its_id}</TableCell>
                  <TableCell className="text-sm">{member.mohalla}</TableCell>
                  <TableCell className="font-mono text-sm">{member.mobile || '-'}</TableCell>
                  <TableCell>
                    {member.has_payment_token_today ? (
                      <Badge className="bg-blue-500">Token #{member.payment_token_number}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-orange-600 border-orange-300">Not Generated</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {member.has_payment_token_today ? (
                      <Badge className="bg-emerald-500">Done for Today</Badge>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600"
                        onClick={() => generatePaymentToken(member)}
                        disabled={generatingFor === member.its_id}
                      >
                        {generatingFor === member.its_id ? (
                          <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-1" />
                        )}
                        Generate Token
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-sm">Page {page} of {totalPages} ({total} members)</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
